import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import { ResultsOwnershipChartPlacement, PlacementSeat } from '../../types'
import { indefiniteArticle, LABELS, STATUS_COLORS, STATUS_LABELS } from '../../pages/AccountabilityCharts/labels'

const MAX_RESPONSIBILITIES = 15
const SOFT_WARN_RESPONSIBILITIES = 5

interface Props {
  chart: ResultsOwnershipChartPlacement
  person: { id: number; full_name: string }
  /** Extra label for the cancel/decline button; the caller decides what it means. */
  cancelLabel?: string
  submitLabel?: string
  onCancel: () => void
  onPlaced: () => void
}

type Mode = 'existing' | 'new'

/**
 * The one placement step shared by the post-creation prompt and the People row
 * action: pick a role that already exists or create one under a parent, list
 * the person's responsibilities, and save it all in a single request.
 *
 * Only roles the viewer may actually edit (and parents they may add under) are
 * offered, so nothing on screen can be rejected by the server for permissions.
 */
export default function ChartPlacementForm({
  chart,
  person,
  cancelLabel = 'Cancel',
  submitLabel = `Add to ${LABELS.feature}`,
  onCancel,
  onPlaced,
}: Props) {
  const editableSeats = useMemo(() => chart.seats.filter(s => s.can_edit), [chart.seats])
  // The Owner's Box is never a parent: it is a root-only box for the owners
  // themselves, not a branch of the reporting structure.
  const parentSeats = useMemo(
    () => chart.seats.filter(s => s.can_add_child && !s.is_owners_box),
    [chart.seats]
  )
  const canCreate = chart.can_add_root || parentSeats.length > 0

  const [mode, setMode] = useState<Mode>(editableSeats.length > 0 ? 'existing' : 'new')
  const [seatSearch, setSeatSearch] = useState('')
  const [seatId, setSeatId] = useState<number | null>(null)
  const [newName, setNewName] = useState('')
  const [parentSeatId, setParentSeatId] = useState<string>(
    chart.can_add_root ? '' : String(parentSeats[0]?.id ?? '')
  )
  const [responsibilities, setResponsibilities] = useState<string[]>([''])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const matches = useMemo(() => {
    const query = seatSearch.trim().toLowerCase()
    if (!query) return editableSeats
    return editableSeats.filter(
      s =>
        s.name.toLowerCase().includes(query) ||
        s.owner_names.some(name => name.toLowerCase().includes(query))
    )
  }, [editableSeats, seatSearch])

  const filled = responsibilities.map(r => r.trim()).filter(Boolean)

  const updateResponsibility = (index: number, value: string) => {
    setResponsibilities(prev => prev.map((r, i) => (i === index ? value : r)))
  }

  const removeResponsibility = (index: number) => {
    setResponsibilities(prev => (prev.length === 1 ? [''] : prev.filter((_, i) => i !== index)))
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setErrors({})
    router.post(
      `/accountability_charts/${chart.id}/placements`,
      {
        user_id: person.id,
        mode: mode === 'new' ? 'new_seat' : 'existing_seat',
        seat_id: mode === 'existing' ? seatId : null,
        name: mode === 'new' ? newName : null,
        parent_seat_id: mode === 'new' ? parentSeatId || null : null,
        responsibilities: filled,
      },
      {
        preserveScroll: true,
        preserveState: true,
        onError: errs => setErrors(errs as Record<string, string>),
        onSuccess: () => onPlaced(),
        onFinish: () => setSubmitting(false),
      }
    )
  }

  const canSubmit =
    !submitting &&
    (mode === 'existing' ? seatId !== null : newName.trim().length > 0 && (chart.can_add_root || parentSeatId !== ''))

  return (
    <form onSubmit={submit} className="space-y-5">
      {errors.base && (
        <p className="text-sm text-pinnacle-red bg-pinnacle-red/10 rounded-lg px-3 py-2">{errors.base}</p>
      )}

      {editableSeats.length > 0 && canCreate && (
        <div className="flex rounded-lg border border-stone/30 overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setMode('existing')}
            className={`flex-1 px-3 py-2 font-medium transition-colors ${
              mode === 'existing' ? 'bg-charcoal text-text-on-dark' : 'text-text-primary hover:bg-stone/10'
            }`}
          >
            Existing {LABELS.seat}
          </button>
          <button
            type="button"
            onClick={() => setMode('new')}
            className={`flex-1 px-3 py-2 font-medium transition-colors ${
              mode === 'new' ? 'bg-charcoal text-text-on-dark' : 'text-text-primary hover:bg-stone/10'
            }`}
          >
            New {LABELS.seat}
          </button>
        </div>
      )}

      {mode === 'existing' ? (
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5">
            Choose {indefiniteArticle(LABELS.seat)} {LABELS.seat.toLowerCase()} on {chart.name}
          </label>
          <input
            type="text"
            value={seatSearch}
            onChange={e => setSeatSearch(e.target.value)}
            placeholder={`Search ${LABELS.seats.toLowerCase()}…`}
            className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
          />
          <div className="mt-2 border border-stone/30 rounded-lg max-h-56 overflow-y-auto divide-y divide-stone/10">
            {matches.length === 0 ? (
              <p className="text-sm text-text-secondary px-3 py-3">
                No {LABELS.seats.toLowerCase()} match that search.
              </p>
            ) : (
              matches.map(seat => (
                <SeatOption
                  key={seat.id}
                  seat={seat}
                  selected={seatId === seat.id}
                  onSelect={() => setSeatId(seat.id)}
                />
              ))
            )}
          </div>
          {errors.seat_id && <p className="text-sm text-pinnacle-red mt-1.5">{errors.seat_id}</p>}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">
              New {LABELS.seat.toLowerCase()} name
            </label>
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="e.g. Director of Operations"
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
            />
            {errors.name && <p className="text-sm text-pinnacle-red mt-1.5">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-1.5">Reports to</label>
            <select
              value={parentSeatId}
              onChange={e => setParentSeatId(e.target.value)}
              className="w-full border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
            >
              {chart.can_add_root && <option value="">Top level (no parent)</option>}
              {parentSeats.map(seat => (
                <option key={seat.id} value={seat.id}>{seat.name}</option>
              ))}
            </select>
            {errors.parent_seat_id && (
              <p className="text-sm text-pinnacle-red mt-1.5">{errors.parent_seat_id}</p>
            )}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-text-primary mb-1.5">
          {LABELS.responsibilities}
        </label>
        {filled.length > SOFT_WARN_RESPONSIBILITIES && (
          <p className="mb-2 px-2 py-1.5 bg-marigold/10 text-marigold text-sm rounded">
            Pinnacle suggests {SOFT_WARN_RESPONSIBILITIES} or fewer {LABELS.responsibilities.toLowerCase()} per {LABELS.seat.toLowerCase()}.
          </p>
        )}
        <div className="space-y-2">
          {responsibilities.map((value, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={value}
                onChange={e => updateResponsibility(index, e.target.value)}
                placeholder={`Add ${indefiniteArticle(LABELS.responsibility)} ${LABELS.responsibility.toLowerCase()}…`}
                className="flex-1 border border-stone/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-charcoal/40 focus:border-charcoal"
              />
              <button
                type="button"
                onClick={() => removeResponsibility(index)}
                aria-label={`Remove ${LABELS.responsibility.toLowerCase()}`}
                className="shrink-0 p-2 rounded-lg text-text-secondary hover:bg-stone/20 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
        {responsibilities.length < MAX_RESPONSIBILITIES && (
          <button
            type="button"
            onClick={() => setResponsibilities(prev => [...prev, ''])}
            className="mt-2 text-sm font-semibold text-pinnacle-red hover:underline"
          >
            + Add another
          </button>
        )}
        {errors.responsibilities && (
          <p className="text-sm text-pinnacle-red mt-1.5">{errors.responsibilities}</p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-text-primary hover:bg-stone/10 rounded-lg transition-colors"
        >
          {cancelLabel}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="px-5 py-2 bg-pinnacle-red text-white text-sm font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors disabled:opacity-50"
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  )
}

function SeatOption({ seat, selected, onSelect }: {
  seat: PlacementSeat
  selected: boolean
  onSelect: () => void
}) {
  const status = STATUS_COLORS[seat.seat_status] || STATUS_COLORS.open
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-3 py-2 transition-colors ${selected ? 'bg-charcoal/10' : 'hover:bg-stone/10'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-text-primary truncate">{seat.name}</span>
        <span className={`text-[12px] font-medium px-2 py-0.5 rounded-full shrink-0 ${status.bg} ${status.text}`}>
          {STATUS_LABELS[seat.seat_status] || seat.seat_status}
        </span>
      </div>
      <p className="text-sm text-text-secondary truncate">
        {seat.owner_names.length > 0
          ? `${LABELS.assignees}: ${seat.owner_names.join(', ')}`
          : `No ${LABELS.assignees.toLowerCase()} yet`}
      </p>
    </button>
  )
}
